/**
 * Admin Logs Utility Functions
 * Helpers for transforming, filtering, and analyzing log data
 */

import {
  AdminLogEntry,
  LegacyLogEntry,
  ActionCategory,
  LogSeverity,
  LogsFilterState,
  LogsStats,
  LogsAnalyticsData,
  LogsActivityByHour,
  LogsActivityByDay,
  LogsActionBreakdown,
  LogsUserActivity,
  CATEGORY_CONFIGS,
} from '../types';

/**
 * Transform legacy log entry to enhanced format
 * This bridges the gap between current backend data and new UI expectations
 */
export function transformLegacyLog(legacy: LegacyLogEntry): AdminLogEntry {
  const payload = typeof legacy.payload === 'string' 
    ? safeJsonParse(legacy.payload) 
    : legacy.payload;
  
  const action = extractActionFromPayload(payload);
  const request = extractRequestFromPayload(payload);
  const result = extractResultFromPayload(payload);
  
  return {
    id: `${legacy.timestamp}-${legacy.user?.id || 'unknown'}`,
    timestamp: legacy.timestamp,
    severity: normalizeSeverity(legacy.severity),
    user: {
      id: legacy.user?.id || 0,
      email: legacy.user?.email || 'unknown@email.com',
      name: legacy.user?.name || 'Unknown User',
      role: payload?.userRole || 'admin',
    },
    organization: payload?.organization || undefined,
    action,
    request,
    payload,
    result,
  };
}

/**
 * Extract action details from payload
 */
function extractActionFromPayload(payload: any): AdminLogEntry['action'] {
  // PRIORITY 1: Use explicit category from payload if provided (from backend)
  const explicitCategory = payload?.category || payload?.action?.category;
  
  // Try to extract from structured payload
  if (payload?.action && payload?.type) {
    return {
      category: explicitCategory || categorizeAction(payload.type, payload.url),
      type: payload.type,
      description: payload.message || formatActionDescription(payload),
    };
  }
  
  // Infer from HTTP method and URL
  if (payload?.method && payload?.url) {
    const category = explicitCategory || categorizeAction(payload.method, payload.url);
    const type = inferActionType(payload.method, payload.url);
    return {
      category,
      type,
      description: payload.message || formatHttpAction(payload.method, payload.url),
    };
  }
  
  // Fallback to message-based extraction
  return {
    category: explicitCategory || 'other',
    type: 'action',
    description: payload?.message || formatObjectAsMessage(payload) || 'Action performed',
  };
}

/**
 * Categorize action based on type and URL
 * NOTE: Order matters! More specific patterns must come first.
 */
export function categorizeAction(type: string, url?: string): ActionCategory {
  const typeL = (type || '').toLowerCase();
  const urlL = (url || '').toLowerCase();
  
  // Auth actions (check first - specific)
  if (typeL.includes('login') || typeL.includes('logout') || typeL.includes('auth')) {
    return 'auth';
  }
  
  // Alert system (MUST come before /brand since alert URLs/types often mention brands)
  if (urlL.includes('/api/v1/alerts') || urlL.includes('/dmg-alerting-system') ||
      urlL.includes('/alert-types') || typeL.includes('alert')) {
    return 'alert';
  }
  
  // Report configuration (MUST come before /brand and generic /report checks)
  if (urlL.includes('/report-config') || urlL.includes('/report-configuration') || 
      typeL.includes('report_config') || typeL.includes('report config')) {
    return 'report';
  }
  
  // Organization actions (MUST come before /brand since org URLs contain /brand/v1/organizations)
  if (urlL.includes('/organization') || typeL.includes('organization')) {
    return 'organization';
  }
  
  // User management / Permissions
  if (urlL.includes('/product_permissions') || urlL.includes('/user_permissions') ||
      urlL.includes('/superadmin') || urlL.includes('/admin/verify') || 
      urlL.includes('/admin/hold') || urlL.includes('/admin/team') ||
      urlL.includes('/admin/level') || urlL.includes('/admin/delete') ||
      typeL.includes('permission') || typeL.includes('superadmin')) {
    return 'user_management';
  }
  
  // GA4 config (before brand since it uses /brand/v1/ga4)
  if (urlL.includes('/ga4') || typeL.includes('ga4')) {
    return 'settings';
  }
  
  // Performance/Paid Ad accounts (MUST come before /brand check since URLs start with /brand/v1/paid_ad)
  if (urlL.includes('/paid_ad') || urlL.includes('/performance') || 
      typeL.includes('performance') || typeL.includes('paid_ad')) {
    return 'performance';
  }
  
  // Brand actions (after organization/ga4/performance checks)
  if ((urlL.includes('/brand') && !urlL.includes('/organization')) || 
      typeL.includes('brand') || urlL.includes('/weight') || urlL.includes('/goal')) {
    return 'brand';
  }
  
  // Drawer/Sidebar items
  if (urlL.includes('/drawer') || urlL.includes('/default-items') || 
      urlL.includes('/items') || urlL.includes('/channels') ||
      urlL.includes('/applications') || urlL.includes('/groupings') ||
      typeL.includes('drawer') || typeL.includes('sidebar')) {
    return 'drawer';
  }
  
  // AI/ML Settings (buddies, models, memories, workflows)
  if (urlL.includes('/liftai') || urlL.includes('/buddies') || 
      urlL.includes('/model') || urlL.includes('/master-memories') ||
      urlL.includes('/workflows') || typeL.includes('buddy') ||
      typeL.includes('model') || typeL.includes('memory') || typeL.includes('workflow')) {
    return 'settings';
  }
  
  // User management (general - after specific permission checks)
  if (urlL.includes('/user') || urlL.includes('/admin/user') || 
      typeL.includes('user') || typeL.includes('admin')) {
    return 'user_management';
  }
  
  // Taxonomy actions
  if (urlL.includes('/taxonomy') || urlL.includes('/txnmy') || 
      urlL.includes('/cdvalue') || typeL.includes('taxonomy')) {
    return 'taxonomy';
  }
  
  // Import actions
  if (urlL.includes('/import') || urlL.includes('/upload') || 
      urlL.includes('/backfill') || urlL.includes('/affluent') ||
      typeL.includes('import')) {
    return 'import';
  }
  
  // Settings actions (general - after specific checks)
  if (urlL.includes('/setting') || urlL.includes('/config') || typeL.includes('setting')) {
    return 'settings';
  }
  
  return 'other';
}

/**
 * Infer action type from HTTP method and URL
 */
function inferActionType(method: string, url: string): string {
  const urlParts = url.split('/').filter(Boolean);
  const resource = urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2] || 'resource';
  
  const cleanResource = resource.replace(/\d+/g, '').replace(/-/g, '_');
  
  switch (method.toUpperCase()) {
    case 'GET':
      return `view_${cleanResource}`;
    case 'POST':
      return `create_${cleanResource}`;
    case 'PUT':
    case 'PATCH':
      return `update_${cleanResource}`;
    case 'DELETE':
      return `delete_${cleanResource}`;
    default:
      return `${method.toLowerCase()}_${cleanResource}`;
  }
}

/**
 * Format HTTP action as readable description
 */
function formatHttpAction(method: string, url: string): string {
  const urlParts = url.split('/').filter(Boolean);
  const resource = urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2] || 'resource';
  
  switch (method.toUpperCase()) {
    case 'GET':
      return `Retrieved ${resource}`;
    case 'POST':
      return `Created ${resource}`;
    case 'PUT':
    case 'PATCH':
      return `Updated ${resource}`;
    case 'DELETE':
      return `Deleted ${resource}`;
    default:
      return `${method} ${resource}`;
  }
}

/**
 * Extract request details from payload
 */
function extractRequestFromPayload(payload: any): AdminLogEntry['request'] | undefined {
  if (!payload?.method && !payload?.url) {
    return undefined;
  }
  
  return {
    method: payload.method?.toUpperCase() || 'GET',
    url: payload.url || '',
    ip: payload.ip || payload.ipAddress,
    userAgent: payload.userAgent,
    duration: payload.duration || payload.responseTime,
    statusCode: payload.statusCode || payload.status,
  };
}

/**
 * Extract result from payload
 */
function extractResultFromPayload(payload: any): AdminLogEntry['result'] | undefined {
  const isError = payload?.isError === true || payload?.error;
  
  return {
    success: !isError,
    error: payload?.error || (isError ? 'Operation failed' : undefined),
    affectedIds: payload?.affectedIds || payload?.ids || undefined,
  };
}

/**
 * Normalize severity string to LogSeverity type
 */
function normalizeSeverity(severity: string): LogSeverity {
  const s = (severity || 'INFO').toUpperCase();
  if (s === 'ERROR' || s === 'ERR') return 'ERROR';
  if (s === 'WARNING' || s === 'WARN') return 'WARNING';
  if (s === 'ALERT') return 'ALERT';
  return 'INFO';
}

/**
 * Safe JSON parse with fallback
 */
function safeJsonParse(str: string): any {
  try {
    return JSON.parse(str);
  } catch {
    return { message: str };
  }
}

/**
 * Format object as readable message
 */
function formatObjectAsMessage(obj: any): string {
  if (!obj || typeof obj !== 'object') return '';
  
  if (obj.name && obj.action) {
    const actionVerb = obj.action === 'add' ? 'Added' : 
                       obj.action === 'update' ? 'Updated' : 
                       obj.action === 'delete' ? 'Deleted' : obj.action;
    return `${actionVerb} ${obj.type || 'item'}: "${obj.name}"`;
  }
  
  const keys = Object.keys(obj).filter(k => 
    !['isError', 'status', 'method', 'url', 'payload', 'error', 'timestamp'].includes(k)
  );
  
  if (keys.length > 0 && keys[0]) {
    const key = keys[0];
    const value = obj[key];
    if (typeof value === 'string') {
      return `${key}: ${value}`;
    }
  }
  
  return '';
}

/**
 * Format action description from payload
 */
function formatActionDescription(payload: any): string {
  if (payload.message) return payload.message;
  
  const parts: string[] = [];
  
  if (payload.action) {
    parts.push(payload.action.charAt(0).toUpperCase() + payload.action.slice(1));
  }
  
  if (payload.type) {
    parts.push(payload.type);
  }
  
  if (payload.name) {
    parts.push(`"${payload.name}"`);
  }
  
  return parts.join(' ') || 'Action performed';
}

/** Failed op: ERROR severity or explicit failure flag on the log. */
export function isFailedLog(log: AdminLogEntry): boolean {
  if (log.severity === 'ERROR') return true;
  if (log.result?.success === false) return true;
  if (log.payload?.isError === true) return true;
  return false;
}

/**
 * Apply filters to log entries
 */
export function filterLogs(
  logs: AdminLogEntry[],
  filters: LogsFilterState
): AdminLogEntry[] {
  return logs.filter(log => {
    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const matchesSearch = 
        log.user.email.toLowerCase().includes(searchLower) ||
        log.user.name.toLowerCase().includes(searchLower) ||
        log.action.description.toLowerCase().includes(searchLower) ||
        log.action.type.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }
    
    // Date range filter
    if (filters.dateRange.startDate) {
      const logDate = new Date(log.timestamp);
      if (logDate < filters.dateRange.startDate) return false;
    }
    if (filters.dateRange.endDate) {
      const logDate = new Date(log.timestamp);
      const endDate = new Date(filters.dateRange.endDate);
      endDate.setHours(23, 59, 59, 999);
      if (logDate > endDate) return false;
    }
    
    // Severity filter
    if (filters.severity.length > 0 && !filters.severity.includes(log.severity)) {
      return false;
    }
    
    // Category filter
    if (filters.categories.length > 0 && !filters.categories.includes(log.action.category)) {
      return false;
    }
    
    // User filter
    if (filters.users.length > 0 && !filters.users.includes(log.user.id)) {
      return false;
    }
    
    // Show only errors (severity ERROR or explicit failure flag)
    if (filters.showOnlyErrors && !isFailedLog(log)) {
      return false;
    }
    
    return true;
  });
}

/**
 * Calculate stats from log entries
 */
export function calculateStats(logs: AdminLogEntry[]): LogsStats {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  const uniqueUsers = new Set(
    logs.map((l) => l.user.id).filter((id) => id != null && id !== 0)
  );
  const uniqueCategories = new Set(
    logs.map((l) => l.action?.category).filter(Boolean)
  );
  
  const totalDuration = logs
    .filter(l => l.request?.duration)
    .reduce((sum, l) => sum + (l.request?.duration || 0), 0);
  const logsWithDuration = logs.filter(l => l.request?.duration).length;
  
  return {
    total: logs.length,
    errors: logs.filter(isFailedLog).length,
    warnings: logs.filter(l => l.severity === 'WARNING').length,
    today: logs.filter(l => new Date(l.timestamp) >= todayStart).length,
    activeUsers: uniqueUsers.size,
    categories: uniqueCategories.size,
    avgResponseTime: logsWithDuration > 0 ? Math.round(totalDuration / logsWithDuration) : undefined,
  };
}

/** Shared date window used by Activity Logs + Analytics (one source of truth). */
export type AnalyticsDateRange = {
  startDate: Date | null;
  endDate: Date | null;
};

/** @deprecated Use AnalyticsDateRange — kept for any external imports. */
export type AnalyticsPeriod = 7 | 30 | 90 | 'all';

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

export function endOfLocalDay(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );
}

/** Inclusive Last N calendar days ending today (N=7 → today + 6 prior days). */
export function getDefaultDateRange(days = 7): { startDate: Date; endDate: Date } {
  const endDate = endOfLocalDay(new Date());
  const startDate = startOfLocalDay(new Date());
  startDate.setDate(startDate.getDate() - (days - 1));
  return { startDate, endDate };
}

/** Local calendar key YYYY-MM-DD (avoids UTC day-shift from toISOString). */
export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function formatDateRangeLabel(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' as const }),
  });
  const endText = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const daySpan =
    Math.round(
      (startOfLocalDay(end).getTime() - startOfLocalDay(start).getTime()) /
        (24 * 60 * 60 * 1000)
    ) + 1;

  const def7 = getDefaultDateRange(7);
  if (
    localDateKey(start) === localDateKey(def7.startDate) &&
    localDateKey(end) === localDateKey(def7.endDate)
  ) {
    return `Last 7 days (${startText} – ${endText})`;
  }
  const def30 = getDefaultDateRange(30);
  if (
    localDateKey(start) === localDateKey(def30.startDate) &&
    localDateKey(end) === localDateKey(def30.endDate)
  ) {
    return `Last 30 days (${startText} – ${endText})`;
  }
  const def90 = getDefaultDateRange(90);
  if (
    localDateKey(start) === localDateKey(def90.startDate) &&
    localDateKey(end) === localDateKey(def90.endDate)
  ) {
    return `Last 90 days (${startText} – ${endText})`;
  }
  if (daySpan === 1) return `On ${endText}`;
  return `${startText} – ${endText}`;
}

export function normalizeDateRange(
  start: Date,
  end: Date
): { startDate: Date; endDate: Date } {
  return {
    startDate: startOfLocalDay(start),
    endDate: endOfLocalDay(end),
  };
}

function percentOf(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

function classifyMutation(
  type: string
): 'creates' | 'updates' | 'deletes' | 'other' {
  const t = (type || '').toLowerCase();
  if (
    t.startsWith('create_') ||
    t.startsWith('add_') ||
    t.includes('_create') ||
    t === 'create' ||
    t === 'add'
  ) {
    return 'creates';
  }
  if (
    t.startsWith('update_') ||
    t.startsWith('edit_') ||
    t.includes('_update') ||
    t === 'update' ||
    t === 'edit'
  ) {
    return 'updates';
  }
  if (
    t.startsWith('delete_') ||
    t.startsWith('remove_') ||
    t.includes('_delete') ||
    t === 'delete' ||
    t === 'remove'
  ) {
    return 'deletes';
  }
  return 'other';
}

function humanizeActionLabel(type: string, description?: string): string {
  const raw = (type || '').trim();
  if (!raw || raw === 'action' || raw === 'unknown') {
    return (description || 'Action').trim();
  }
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Slice logs to the shared page date window (same range Activity Logs fetches).
 */
function filterLogsByDateRange(
  logs: AdminLogEntry[],
  dateRange?: AnalyticsDateRange
): {
  current: AdminLogEntry[];
  previous: AdminLogEntry[];
  periodLabel: string;
  daySpan: number;
  periodStart: Date;
  periodEnd: Date;
  hasPreviousSample: boolean;
} {
  const resolved =
    dateRange?.startDate && dateRange?.endDate
      ? normalizeDateRange(dateRange.startDate, dateRange.endDate)
      : getDefaultDateRange(7);

  const periodStart = resolved.startDate;
  const periodEnd = resolved.endDate;
  const daySpan = Math.max(
    1,
    Math.round(
      (startOfLocalDay(periodEnd).getTime() - startOfLocalDay(periodStart).getTime()) /
        (24 * 60 * 60 * 1000)
    ) + 1
  );

  const previousEnd = new Date(periodStart.getTime() - 1);
  const previousStart = startOfLocalDay(new Date(periodStart));
  previousStart.setDate(previousStart.getDate() - daySpan);

  const inRange = (t: number, start: Date, end: Date) =>
    t >= start.getTime() && t <= end.getTime();

  const current = logs.filter((l) =>
    inRange(new Date(l.timestamp).getTime(), periodStart, periodEnd)
  );
  const previous = logs.filter((l) =>
    inRange(new Date(l.timestamp).getTime(), previousStart, previousEnd)
  );

  return {
    current,
    previous,
    periodLabel: formatDateRangeLabel(periodStart, periodEnd),
    daySpan,
    periodStart,
    periodEnd,
    hasPreviousSample: previous.length > 0,
  };
}

function buildTrend(
  current: number,
  previous: number,
  hasPreviousSample: boolean
): { direction: 'up' | 'down' | 'neutral'; value: string } | undefined {
  // Avoid fake "+100%" when the prior window wasn't loaded.
  if (!hasPreviousSample) return undefined;
  if (previous === 0 && current === 0) {
    return { direction: 'neutral', value: '0%' };
  }
  if (previous === 0) {
    return { direction: 'up', value: 'New' };
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { direction: 'neutral', value: '0%' };
  return {
    direction: pct > 0 ? 'up' : 'down',
    value: `${pct > 0 ? '+' : ''}${pct}%`,
  };
}

function summarizeSlice(logs: AdminLogEntry[]) {
  const errorCount = logs.filter(isFailedLog).length;
  const warningCount = logs.filter((l) => l.severity === 'WARNING').length;
  const successCount = logs.length - errorCount;
  const activeUsers = new Set(
    logs.map((l) => l.user.id).filter((id) => id != null && id !== 0)
  ).size;
  return {
    totalActions: logs.length,
    errorCount,
    warningCount,
    successCount,
    activeUsers,
    errorRate: percentOf(errorCount, logs.length),
    successRate: percentOf(successCount, logs.length),
  };
}

/**
 * Generate analytics for the shared page date range (same window as Activity Logs).
 */
export function generateAnalytics(
  logs: AdminLogEntry[],
  dateRange?: AnalyticsDateRange
): LogsAnalyticsData {
  const {
    current,
    previous,
    periodLabel,
    daySpan,
    periodStart,
    periodEnd,
    hasPreviousSample,
  } = filterLogsByDateRange(logs, dateRange);
  const currentSummary = summarizeSlice(current);
  const previousSummary = summarizeSlice(previous);

  // Activity by local hour
  const activityByHour: LogsActivityByHour[] = [];
  const hourCounts = new Map<number, { count: number; errors: number }>();
  for (let h = 0; h < 24; h++) hourCounts.set(h, { count: 0, errors: 0 });
  current.forEach((l) => {
    const hour = new Date(l.timestamp).getHours();
    const bucket = hourCounts.get(hour)!;
    bucket.count++;
    if (isFailedLog(l)) bucket.errors++;
  });
  hourCounts.forEach((data, hour) => activityByHour.push({ hour, ...data }));
  activityByHour.sort((a, b) => a.hour - b.hour);

  const peak = activityByHour.reduce(
    (best, row) =>
      row.count > best.count ? { hour: row.hour, count: row.count } : best,
    { hour: 0, count: 0 }
  );

  // Activity by local calendar day across the selected period
  const chartDays = Math.min(daySpan, 90);
  const activityByDay: LogsActivityByDay[] = [];
  const dayCounts = new Map<string, { count: number; errors: number }>();
  for (let d = chartDays - 1; d >= 0; d--) {
    const date = startOfLocalDay(periodEnd);
    date.setDate(date.getDate() - d);
    // Only include days inside [periodStart, periodEnd]
    if (date.getTime() < periodStart.getTime()) continue;
    dayCounts.set(localDateKey(date), { count: 0, errors: 0 });
  }
  current.forEach((l) => {
    const dateStr = localDateKey(new Date(l.timestamp));
    const bucket = dayCounts.get(dateStr);
    if (!bucket) return;
    bucket.count++;
    if (isFailedLog(l)) bucket.errors++;
  });
  Array.from(dayCounts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([date, data]) => activityByDay.push({ date, ...data }));

  // Category breakdown
  const categoryCounts = new Map<ActionCategory, number>();
  current.forEach((l) => {
    const cat = l.action?.category || 'other';
    categoryCounts.set(cat, (categoryCounts.get(cat) || 0) + 1);
  });
  const actionBreakdown: LogsActionBreakdown[] = Array.from(
    categoryCounts.entries()
  )
    .map(([category, count]) => ({
      category,
      count,
      percentage: percentOf(count, current.length),
    }))
    .sort((a, b) => b.count - a.count);

  // Top action types (humanized labels, precise share of period)
  const actionTypeCounts = new Map<string, number>();
  current.forEach((l) => {
    const key = humanizeActionLabel(l.action?.type, l.action?.description);
    actionTypeCounts.set(key, (actionTypeCounts.get(key) || 0) + 1);
  });
  const topActions = Array.from(actionTypeCounts.entries())
    .map(([action, count]) => ({
      action,
      count,
      percentage: percentOf(count, current.length),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  // Creates / updates / deletes
  const mutations = { creates: 0, updates: 0, deletes: 0, other: 0 };
  current.forEach((l) => {
    mutations[classifyMutation(l.action?.type || '')] += 1;
  });

  // Severity breakdown (always include all severities for stable UI)
  const severityCounts = new Map<LogSeverity, number>();
  current.forEach((l) => {
    severityCounts.set(l.severity, (severityCounts.get(l.severity) || 0) + 1);
  });
  const severityBreakdown = (
    ['INFO', 'WARNING', 'ERROR', 'ALERT'] as LogSeverity[]
  ).map((severity) => {
    const count = severityCounts.get(severity) || 0;
    return {
      severity,
      count,
      percentage: percentOf(count, current.length),
    };
  });

  // Top users (unique by id; skip unknown id 0 when name also unknown duplicates)
  const userActivity = new Map<string, LogsUserActivity>();
  current.forEach((l) => {
    const key =
      l.user.id && l.user.id !== 0
        ? `id:${l.user.id}`
        : `email:${(l.user.email || '').toLowerCase()}`;
    const existing = userActivity.get(key);
    if (existing) {
      existing.actionCount++;
      if (isFailedLog(l)) existing.errorCount++;
      if (new Date(l.timestamp) > new Date(existing.lastActive)) {
        existing.lastActive = l.timestamp;
      }
    } else {
      userActivity.set(key, {
        userId: l.user.id,
        userName: l.user.name || 'Unknown User',
        userEmail: l.user.email || '',
        actionCount: 1,
        errorCount: isFailedLog(l) ? 1 : 0,
        lastActive: l.timestamp,
      });
    }
  });
  const topUsers = Array.from(userActivity.values())
    .sort((a, b) => b.actionCount - a.actionCount)
    .slice(0, 10);

  // Mean daily volume over the full calendar period (not only days with activity)
  const avgActionsPerDay =
    daySpan > 0
      ? Math.round((currentSummary.totalActions / daySpan) * 10) / 10
      : 0;
  const actionsPerAdmin =
    currentSummary.activeUsers > 0
      ? Math.round(
          (currentSummary.totalActions / currentSummary.activeUsers) * 10
        ) / 10
      : 0;

  // Coverage against the shared date window
  let coverageIncomplete = false;
  let coverageNote: string | undefined;
  if (logs.length === 0) {
    coverageIncomplete = true;
    coverageNote =
      'No logs loaded for this date range yet. Change the date range above or refresh.';
  } else {
    const loadedTimes = logs.map((l) => new Date(l.timestamp).getTime());
    const loadedMin = Math.min(...loadedTimes);
    const loadedMax = Math.max(...loadedTimes);
    const loadedSpanDays =
      Math.round(
        (startOfLocalDay(new Date(loadedMax)).getTime() -
          startOfLocalDay(new Date(loadedMin)).getTime()) /
          (24 * 60 * 60 * 1000)
      ) + 1;
    const startGapMs = loadedMin - periodStart.getTime();
    if (loadedSpanDays < daySpan || startGapMs > 36 * 60 * 60 * 1000) {
      coverageIncomplete = true;
      coverageNote = `Showing ${currentSummary.totalActions.toLocaleString()} loaded actions in this range. Scroll/load more on Activity Logs if you expect additional history.`;
    }
  }

  if (!coverageNote && current.length > 0) {
    coverageNote = `${currentSummary.totalActions.toLocaleString()} actions in this date range · same window as Activity Logs`;
  }

  return {
    activityByHour,
    activityByDay,
    actionBreakdown,
    topActions,
    severityBreakdown,
    topUsers,
    mutations,
    totalActions: currentSummary.totalActions,
    successCount: currentSummary.successCount,
    errorCount: currentSummary.errorCount,
    warningCount: currentSummary.warningCount,
    activeUsers: currentSummary.activeUsers,
    errorRate: currentSummary.errorRate,
    successRate: currentSummary.successRate,
    avgActionsPerDay,
    actionsPerAdmin,
    peakHour: peak.hour,
    peakHourCount: peak.count,
    periodLabel,
    daySpan,
    rangeStart: periodStart.toISOString(),
    rangeEnd: periodEnd.toISOString(),
    coverageIncomplete,
    coverageNote,
    trends: {
      totalActions: buildTrend(
        currentSummary.totalActions,
        previousSummary.totalActions,
        hasPreviousSample
      ),
      errorRate: buildTrend(
        currentSummary.errorRate,
        previousSummary.errorRate,
        hasPreviousSample
      ),
      activeUsers: buildTrend(
        currentSummary.activeUsers,
        previousSummary.activeUsers,
        hasPreviousSample
      ),
      successRate: buildTrend(
        currentSummary.successRate,
        previousSummary.successRate,
        hasPreviousSample
      ),
    },
  };
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string, format: 'full' | 'date' | 'time' | 'relative' = 'full'): string {
  const date = new Date(timestamp);
  
  if (format === 'relative') {
    return formatRelativeTime(date);
  }
  
  if (format === 'date') {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  }
  
  if (format === 'time') {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  }
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

/**
 * Format relative time
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  
  return formatTimestamp(date.toISOString(), 'date');
}

/**
 * Get category display info
 */
export function getCategoryInfo(category: ActionCategory) {
  return CATEGORY_CONFIGS.find(c => c.id === category) || CATEGORY_CONFIGS[CATEGORY_CONFIGS.length - 1];
}

/**
 * Export logs to CSV format
 */
export function exportToCSV(logs: AdminLogEntry[], includePayload: boolean = false): string {
  const headers = [
    'Timestamp',
    'User Name',
    'User Email',
    'Severity',
    'Category',
    'Action Type',
    'Description',
    'Method',
    'URL',
    'Success',
    ...(includePayload ? ['Payload'] : [])
  ];
  
  const rows = logs.map(log => [
    log.timestamp,
    log.user.name,
    log.user.email,
    log.severity,
    log.action.category,
    log.action.type,
    `"${log.action.description.replace(/"/g, '""')}"`,
    log.request?.method || '',
    log.request?.url || '',
    log.result?.success ? 'Yes' : 'No',
    ...(includePayload ? [JSON.stringify(log.payload || {})] : [])
  ]);
  
  return [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
}

/**
 * Export logs to JSON format
 */
export function exportToJSON(logs: AdminLogEntry[]): string {
  return JSON.stringify(logs, null, 2);
}

/**
 * Highlight search text in a string
 */
export function highlightText(text: string, search: string): { parts: { text: string; highlight: boolean }[] } {
  if (!search || !text) {
    return { parts: [{ text, highlight: false }] };
  }
  
  const regex = new RegExp(`(${search})`, 'gi');
  const parts = text.split(regex);
  
  return {
    parts: parts.map(part => ({
      text: part,
      highlight: part.toLowerCase() === search.toLowerCase()
    }))
  };
}
